import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, Info } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
export const ABO_HINTS = {
  1: "Dein Monatsabo beinhaltet 4 Trainingseinheiten à 60 Minuten pro Monat an einem festen Wochentag und zu einer festen Uhrzeit. Auch in Monaten mit fünf entsprechenden Wochentagen sind maximal 4 Einheiten enthalten.",
  2: "Dein Monatsabo beinhaltet 8 Trainingseinheiten à 60 Minuten pro Monat an zwei festen Wochentagen und zu festen Uhrzeiten. Auch in Monaten mit fünf Kalenderwochen sind maximal 8 Einheiten enthalten.",
};
export const perWeekOf = (packageType) => (String(packageType).endsWith("2x") ? 2 : 1);
const WD = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
export const deDay = (s) => { const [y, m, d] = s.split("-").map(Number); return `${WD[new Date(y, m - 1, d).getDay()]}, ${String(d).padStart(2, "0")}.${String(m).padStart(2, "0")}.${y}`; };
const sel = "w-full h-12 px-3 bg-[#0A0A0A] border border-white/20 text-white rounded-md";

export function AboHint({ perWeek, testId = "abo-hint" }) {
  return (
    <div className="flex gap-2 items-start bg-[#c1ff72]/10 border border-[#c1ff72]/40 rounded-lg p-3 text-sm text-[#E4E4E7]" data-testid={testId}>
      <Info className="w-4 h-4 text-[#c1ff72] shrink-0 mt-0.5" /><p>{ABO_HINTS[perWeek]}</p>
    </div>
  );
}

export function AboDates({ preview, testId = "abo-dates" }) {
  return (
    <div className="space-y-2" data-testid={testId}>
      <p className="text-sm font-semibold text-white">Enthaltene Termine im {preview.month_label} ({preview.units} × 60 Min.)</p>
      <ul className="grid sm:grid-cols-2 gap-1.5">{preview.dates.map((d) => (
        <li key={d.date + d.time} className="text-sm bg-[#0A0A0A] border border-white/10 rounded px-3 py-2 flex items-center gap-2"><CalendarCheck className="w-4 h-4 text-[#c1ff72]" />{deDay(d.date)} · {d.time}–{d.end_time} Uhr{d.holiday && <span className="text-xs text-sky-300" data-testid={`abo-date-holiday-${d.date}`}>({d.holiday})</span>}</li>))}</ul>
      {preview.excluded.length > 0 && <p className="text-xs text-yellow-300" data-testid={`${testId}-excluded`}>Nicht enthalten (jeweils der 5. {[...new Set(preview.excluded.map((d) => d.weekday_name))].join(" bzw. ")} im Monat): {preview.excluded.map((d) => `${deDay(d.date)} ${d.time} Uhr`).join(", ")}.</p>}
    </div>
  );
}

export function AboPlanner({ packageType, trainingType, onChange }) {
  const n = perWeekOf(packageType);
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("");
  const [picks, setPicks] = useState(Array(n).fill(""));
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    setData(null);
    fetch(`${API_URL}/api/kunden/abos/plan-options?package_type=${packageType}&training_type=${trainingType}${month ? `&month=${month}` : ""}`)
      .then((r) => r.json()).then((d) => { setData(d); if (!month) setMonth(d.month); }).catch(() => toast.error("Freie Zeiten konnten nicht geladen werden"));
  }, [packageType, trainingType, month]);
  useEffect(() => { setPicks(Array(n).fill("")); setPreview(null); onChange(null); }, [packageType, trainingType, month, n]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const days = picks.map((p) => p.split("|")[0]);
    if (!month || !picks.every(Boolean) || new Set(days).size !== n) { setPreview(null); onChange(null); return; }
    const slots = picks.map((p) => { const [w, t] = p.split("|"); return { weekday: Number(w), time: t }; });
    fetch(`${API_URL}/api/kunden/abos/plan-preview`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ package_type: packageType, training_type: trainingType, month, slots }) })
      .then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.detail); setPreview(d); onChange({ month, slots, preview: d }); })
      .catch((e) => { toast.error(e.message || "Termine nicht verfügbar"); setPreview(null); onChange(null); });
  }, [picks]); // eslint-disable-line react-hooks/exhaustive-deps
  const days = picks.map((p) => p.split("|")[0]);
  return (
    <div className="space-y-4 bg-[#1A1A1A] rounded-xl p-5 border border-white/10" data-testid="abo-planner">
      <h3 className="font-semibold text-lg">Deine festen Trainingszeiten</h3>
      <AboHint perWeek={n} testId="abo-hint-planner" />
      <label className="block text-sm text-[#A1A1AA]">Startmonat
        <select value={month} onChange={(e) => setMonth(e.target.value)} className={`${sel} mt-1`} data-testid="abo-month-select">
          {(data?.months || []).map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}</select></label>
      {!data ? <p className="text-sm text-[#71717A]">Freie Zeiten werden geprüft …</p> : !data.items.length
        ? <p className="text-sm text-yellow-300" data-testid="abo-planner-empty">In diesem Monat ist leider keine Zeit an allen 4 Terminen frei. Bitte wähle einen anderen Monat oder kontaktiere uns für eine individuelle Lösung.</p>
        : picks.map((p, i) => (
          <label key={i} className="block text-sm text-[#A1A1AA]">{n > 1 ? `${i + 1}. fester Trainingstermin` : "Fester Wochentag und Uhrzeit"}
            <select value={p} onChange={(e) => setPicks(picks.map((x, j) => (j === i ? e.target.value : x)))} className={`${sel} mt-1`} data-testid={`abo-plan-slot-${i + 1}`}>
              <option value="">Bitte wählen …</option>
              {data.items.filter((o) => !days.some((d, j) => j !== i && d === String(o.weekday))).map((o) => <option key={`${o.weekday}|${o.time}`} value={`${o.weekday}|${o.time}`}>{o.weekday_name}, {o.time}–{o.end_time} Uhr</option>)}
            </select></label>))}
      {n > 1 && <p className="text-xs text-[#71717A]">Bitte zwei verschiedene Wochentage wählen. Angezeigt werden nur Zeiten, die an allen 4 Terminen frei sind.</p>}
      {preview && <AboDates preview={preview} />}
    </div>
  );
}
